/**
 * Document upload component for merchant onboarding
 */

import { Button, color } from "@funded-here-interface/common";
import { Text, Button as MantineButton, Modal } from "@mantine/core";
import { FC, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@funded-here-interface/investor-onboarding/src/store";
import { useStyle } from "../../styles/BorrowerDocUpload";
import { data } from "./Data";
import {
  File as FileEnum,
  OnboardingStatusEnum,
} from "@funded-here-interface/common/src/constant/enum";
import FileUploadComponent from "../../components/FileUpload/FileUpload";
import {
  setUserFirstName,
  setUserLastName,
} from "@funded-here-interface/common/src/features/auth/authSlice_Investor";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";
import axios from "axios";
import {
  getBaseUrl,
  Microservice,
} from "@funded-here-interface/common/src/constant/baseUrl";
import { DraftData, FileData } from "./types";
import {
  hydrateDocumentsToRedux,
  getDocumentAction,
  validateDocuments,
  getExistingFilesForType,
} from "./utils/documentUtils";
import { validateBizProfileForm } from "../BizProfile/utils/bizProfileUtils";
import {
  UPLOAD_INFO_TEXT_LINE1,
  UPLOAD_INFO_TEXT_LINE2,
} from "./documentConfig";
import { TabComponentProps } from "../SalesOnboarding/types";

const BorrowerDocsUpload: FC<TabComponentProps> = ({
  isEditable,
  merchantId,
  onDirtyChange,
  onSaveDraft,
  onResetForm,
  onboardingStatus,
}) => {
  const { classes } = useStyle();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token } = useSelector((state: RootState) => state.auth);
  const [isLoading, setLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { userFirstName, userLastName } = useSelector(
    (state: RootState) => state.Borrep,
  );

  const {
    company: companyName,
    regNumber: registrationNumber,
    legalStructure,
    industryGroup,
    typeOfInstitute: institutionType,
    countryOfIncorp: countryOfIncorporation,
    countryOfDomicile,
    address,
    phoneNumber,
    postal: postalcode,
    website: companyWebsite,
    incorporationDate: companyStartDate,
  } = useSelector((state: RootState) => state.borBiz);

  const userId = merchantId.toString();

  const hasFetched = useRef(false);
  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isDirty, setIsDirty] = useState(false);
  const [shouldMarkDirty, setShouldMarkDirty] = useState(true);
  const [initialDraftData, setInitialDraftData] = useState<DraftData | null>(
    null,
  );

  const fetchDraft = async (userId: number) => {
    try {
      const params = {
        userId: userId,
        token: token,
      };

      const result = await getOrgDraftMutation.mutateAsync(params);
      return result;
    } catch (error) {
      console.error("Error fetching draft:", error);
    }
  };

  useEffect(() => {
    let isMounted = true;
    if (!isEditable) {
      setIsDirty(false);
      onDirtyChange(false);
    }
    const loadData = async () => {
      if (!hasFetched.current && !isLoading) {
        setLoading(true);
        setShouldMarkDirty(false);
        try {
          const data = await fetchDraft(parseInt(userId));
          if (data && isMounted) {
            setInitialDraftData(data);
            hydrateDocumentsToRedux(dispatch, data);
            setIsDirty(false);
            onDirtyChange(false);
            hasFetched.current = true;
          }
        } catch (error) {
          toast.error(`Error loading draft: ${error}`);
        } finally {
          setLoading(false);
          setShouldMarkDirty(true);
        }
      }
    };

    loadData();
  }, [dispatch, userId, token]);

  const {
    business,
    share,
    bank: bankDoc,
    balance,
    proof,
    certificateOfIncorporation,
    supportingDocumentsWithSuppliers,
  } = useSelector((state: RootState) => state.bordoc);

  const handleFileChange = (fileType: FileEnum, files: FileData[]) => {
    const action = getDocumentAction(fileType, files);
    if (action) {
      dispatch(action);
    }
    if (shouldMarkDirty) {
      setIsDirty(true);
      onDirtyChange(true);
    }
  };

  const saveDraft = async (showToast: boolean = true) => {
    return saveOrgDraftMutation.mutateAsync(
      {
        userId: parseInt(userId),
        token: token,
        businessProfile: business,
        shareHoldersDirectorsRegisters: share,
        proofOfAddress: proof,
        bankStatement: bankDoc,
        balanceSheetPAndLStatement: balance,
        certificateOfIncorporation: certificateOfIncorporation,
        supportingDocumentsWithSuppliers: supportingDocumentsWithSuppliers,
      },
      {
        onSuccess: () => {
          setIsDirty(false);
          onDirtyChange(false);
          setInitialDraftData({
            businessProfile: business,
            shareHoldersDirectorsRegisters: share,
            proofOfAddress: proof,
            bankStatement: bankDoc,
            balanceSheetPAndLStatement: balance,
            certificateOfIncorporation: certificateOfIncorporation,
            supportingDocumentsWithSuppliers: supportingDocumentsWithSuppliers,
          });
          if (showToast) {
            toast.success("Draft Saved", { autoClose: 2000 });
          }
        },
        onError: (e) => {
          toast.error((e as Error).message);
        },
      },
    );
  };

  const resetForm = () => {
    if (initialDraftData) {
      hydrateDocumentsToRedux(dispatch, initialDraftData);
      setIsDirty(false);
      onDirtyChange(false);
    }
  };

  useEffect(() => {
    onSaveDraft(saveDraft);
    onResetForm(resetForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSaveDraft, onResetForm]);

  const handleSendEmail = async (
    userId: string,
    onboardingStatus: OnboardingStatusEnum,
  ) => {
    try {
      const response = await axios.post(
        `${getBaseUrl(Microservice.TRADE_DIRECTORY)}/trade-directory/organization/change-status/${userId}`,
        {
          onboardingStatus: onboardingStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success(
        "Onboarding draft successfully sent to the merchant for review.",
      );
    } catch (error) {
      toast.error(
        "Onboarding draft failed to be sent to the merchant for review.",
      );
    }
  };

  const handleNext = async () => {
    saveDraft(false);

    const missingFiles = validateDocuments({
      business,
      share,
      bankDoc,
      proof,
      balance,
      certificateOfIncorporation,
      supportingDocumentsWithSuppliers,
    });

    if (missingFiles.length > 0) {
      toast.error(`Please upload ${missingFiles.join(", ")}`);
      return;
    }

    setIsDirty(false);
    onDirtyChange(false);

    // TODO: Implement submission logic
    // The following data should be used when implementing the API call:
    // - Document file paths from Redux state: proof, business, share, bankDoc, balance,
    //   certificateOfIncorporation, supportingDocumentsWithSuppliers
    // - Bank details from state.BorBank: bankName, swiftCode, accountNumber, accountType, accountHolderName
    // - Country info from state.borBiz: countryOfDomicile, countryOfIncorp
    // - User names (already dispatched below): userFirstName, userLastName

    dispatch(
      setUserFirstName({
        userFirstName: userFirstName ?? null,
      }),
    );
    dispatch(
      setUserLastName({
        userLastName: userLastName ?? null,
      }),
    );

    setLoading(true);
  };

  const handleCompleteDraft = () => {
    // Validate business profile fields
    const { valid: bizProfileValid, errors: bizProfileErrors } =
      validateBizProfileForm({
        companyName: companyName || "",
        registrationNumber: registrationNumber || "",
        companyStartDate: companyStartDate || "",
        legalStructure: legalStructure || "",
        industryGroup: industryGroup || "",
        institutionType: institutionType || "",
        countryOfIncorporation: countryOfIncorporation || "",
        countryOfDomicile: countryOfDomicile || "",
        address: address || "",
        phoneNumber: phoneNumber || "",
        postalcode: postalcode || "",
        companyWebsite: companyWebsite || "",
      });

    if (!bizProfileValid) {
      // Find the first error message to display
      const firstError = Object.values(bizProfileErrors).find(
        (error) => error && error.length > 0,
      );
      toast.error(
        firstError || "Please complete all required fields in Business Profile",
      );
      return;
    }

    // Validate documents
    const missingFiles = validateDocuments({
      business,
      share,
      bankDoc,
      proof,
      balance,
      certificateOfIncorporation,
      supportingDocumentsWithSuppliers,
    });

    if (missingFiles.length > 0) {
      toast.error(`Please upload ${missingFiles.join(", ")}`);
      return;
    }

    // All validations passed, show confirmation modal
    setShowConfirmModal(true);
  };

  return (
    <div>
      <div
        className={classes.stepContent}
        style={{ marginTop: "0px", background: "#fff" }}
      >
        <p className={classes.headinfo}>
          {UPLOAD_INFO_TEXT_LINE1}
          <br />
          {UPLOAD_INFO_TEXT_LINE2}
        </p>
        {data.map((item) => (
          <FileUploadComponent
            isEditable={isEditable}
            key={item.number}
            number={item.number}
            fileId={item.id}
            FileName={item.heading}
            existingFiles={getExistingFilesForType(item.id, {
              certificateOfIncorporation,
              proof,
              share,
              bankDoc,
              business,
              balance,
              supportingDocumentsWithSuppliers,
            })}
            onFileChange={(files) => handleFileChange(item.id, files)}
          />
        ))}

        <div className={classes.buttonWrapper}>
          <Button
            onClick={() => saveDraft(true)}
            textColor={color.WHITE}
            backgroundColor={color.LOANTEXT}
            children="Save Draft"
            width="45%"
            disabled={!isEditable}
          />
          <Button
            onClick={handleCompleteDraft}
            textColor={color.WHITE}
            backgroundColor={color.FHGREEN}
            children="Complete Draft"
            width="45%"
            disabled={!isEditable}
          />
        </div>
        <Modal
          opened={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          title="Draft Completion Confirmation"
          styles={{
            title: { fontWeight: 700 },
          }}
        >
          <Text>
            By confirming, a notification will be sent to the merchant to review
            and submit the onboarding information. You will no longer be able to
            make changes unless the merchant returns the draft.
            <br />
            <br />
            Do you want to proceed?
          </Text>
          <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
            <MantineButton
              onClick={async () => {
                setShowConfirmModal(false);
                // Must await to ensure draft is saved before changing status
                await saveDraft();
                await handleSendEmail(
                  userId,
                  onboardingStatus ===
                    OnboardingStatusEnum.ONBOARDING_RESUBMISSION_IN_DRAFT
                    ? OnboardingStatusEnum.PENDING_RESUBMISSION
                    : OnboardingStatusEnum.PENDING_SUBMISSION,
                );
                navigate("/onboarding");
              }}
            >
              Confirm
            </MantineButton>
            <MantineButton
              onClick={() => setShowConfirmModal(false)}
              variant="outline"
            >
              Cancel
            </MantineButton>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default BorrowerDocsUpload;
